import React from 'react';
import reactCookie from 'react-cookie';
import { APP_SAVE_INITIAL_DATA } from '../src/constants/ActionTypes';
import { envValue } from '../config/envUtils'
import { IsTestEnv } from '../src/utils/configuration-utils';
import moment from 'moment';
import { postRequestToSplunk, postResponseToSplunkSuccess, postResponseToSplunkFailed } from '../src/services/logging/splunklogging'

import { httpClient } from './utils';
import { get_token as legacyGetToken, userHasAccessToRequest } from './legacy';
import { OAuthActions } from "./redux-oauth2-actions";
import { ACCESS_POINTS } from '../src/constants/AccessPoints'
import { SAVE_LEVEL } from '../src/constants/Level'

const AUTH_TOKEN = envValue('AUTH_TOKEN');
const FRONTMAN_URL = envValue('FRONTMAN_URL');
const isTestEnv = IsTestEnv();

const config = {
    url: '',
    token: '/v1/oauth/token',
    client_id: null,
    client_secret: null,
    providers: {
        github: '/auth/github',
    }
};

export default (_config) => {
    Object.assign(config, _config);
};

function fetch_user(token) {
    return httpClient.get(`${config.token}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
}

export const actions = {
    cancel() {
        return {
            type: OAuthActions.OAUTH_CANCELED
        }
    },
    setAccessLevel(level) {
        return {
            type: 'OAUTH_SET_ACCESS_LEVELS',
            userPermissionLevels: level
        }
    },
    defaultHotel(hotel) {
        return {
            type: 'OAUTH_DEFAULTHOTEL',
            hotel: hotel
        }
    },
    setChain(chain, name) {
        return {
            type: 'LOGIN_SET_CHAINID',
            chain,
            name
        }
    },
    username(username) {
        return {
            type: 'OAUTH_USERNAME',
            username: username
        }
    },
    error(error) {
        return {
            type: 'OAUTH_ERROR',
            payload: error,
        }
    },
    start() {
        return {
            type: 'OAUTH_START',
        }
    },
    load_user(user) {
        return {
            type: 'OAUTH_LOAD_USER',
            payload: user,
        }
    },
    get_token(creds, cb, newLogin = true) {
        add_axios_interceptors()
        return (newLogin ? getToken : legacyGetToken).apply(this, arguments);
    },
    saveAccessPoints(accesspointType, accesspoint) {
        return {
            type: 'OAUTH_LOAD_ACCESS_POINTS',
            [accesspointType]: accesspoint
        }
    },
    complete(data, cb, token) {
        return dispatch => {
            // save token
            dispatch(actions.save_token(data, token));
            dispatch(actions.save_initialData(_.get(data, 'initialState.ApplicationResults', data)));
            add_axios_header(data);
            // sync user
            // dispatch(actions.sync_user(token.access_token, cb));
            dispatch({ type: 'OAUTH_COMPLETE' });
            cb();
        };
    },
    save_initialData(data) {
        // app saves the initialData sent by the backend -> store/AppStore.js
        return {
            type: APP_SAVE_INITIAL_DATA,
            payload: data
        };
    },
    save_token(data, token) {
        // save token.access_token
        const dataToSaveAsCookie = {
            access_token: token || data.access_token,
            expires_in: data.expires_in || 720
        };
        reactCookie.save('auth', JSON.stringify(dataToSaveAsCookie), { path: '/' });
        return {
            type: 'OAUTH_SAVE_TOKEN',
            payload: data
        }
    },
    signout() {
        return dispatch => {
            sessionStorage.removeItem('token');
            reactCookie.remove('auth', { path: '/' });
            dispatch({
                type: 'OAUTH_SIGNOUT'
            })
        }
    },
    sync_user(token, cb) {
        return dispatch => {
            fetch_user(token).then(res => {
                dispatch(actions.load_user(res.data))
                cb(res.data);
            }).catch(actions.error)
        }
    },
};

export const isAuthReducer =
    (state = { authenticating: false, user: null, error: null, labelAccess: true, themeAccess: true, configAccess: true, userPermissionLevels: [SAVE_LEVEL.HOTEL] }, actions) => {
        switch (actions.type) {
            case 'OAUTH_START':
                return { authenticating: true };
            case OAuthActions.OAUTH_CANCELED:
                return { authenticating: false };
            case 'OAUTH_ERROR':
                return { authenticating: false };
            case 'OAUTH_LOAD_USER':
                return { authenticating: false };
            case 'OAUTH_COMPLETE':
                return { authenticating: false };
            case 'OAUTH_SIGNOUT':
                return { authenticating: false };
            case 'OAUTH_SET_ACCESS_LEVELS':
                return { ...state, userPermissionLevels: actions.level };
            default:
                return state;
        }
    }

export const reducer = {
    oauth(state = { isAuthenticated: false, user: null, error: null, labelAccess: true, themeAccess: true, configAccess: true, userPermissionLevels: [SAVE_LEVEL.HOTEL], roles: [], assignedChains: [] }, actions) {
        switch (actions.type) {
            case 'OAUTH_START':
                return { ...state, isAuthenticated: false, user: null, error: null };
            case 'OAUTH_LOAD_ACCESS_POINTS':
                return { ...state, [actions.accessPointType]: actions.accessPointValue };
            case 'OAUTH_DEFAULTHOTEL':
                return { ...state, defaultHotel: actions.hotel };
            case 'OAUTH_USERNAME':
                return { ...state, username: actions.username };
            case OAuthActions.OAUTH_CANCELED:
                return { isAuthenticated: false, user: null, error: 'user canceled' };
            case 'OAUTH_ERROR':
                return { isAuthenticated: false, user: null, error: actions.payload };
            case 'OAUTH_LOAD_USER':
                return { isAuthenticated: true, user: actions.payload, error: null };
            case 'OAUTH_COMPLETE':
                return { ...state, isAuthenticated: true, user: actions.payload, error: null };
            case 'OAUTH_SIGNOUT':
                return { isAuthenticated: false, user: null, error: null };
            case 'OAUTH_SET_ACCESS_LEVELS':
                return { ...state, userPermissionLevels: actions.userPermissionLevels };
            case 'OAUTH_ROLES':
                return { ...state, roles: actions.roles };
            case 'OAUTH_ASSIGNED_CHAINS':
                return { ...state, assignedChains: actions.chains }
            default:
                return state;
        }
    }
};

const ERROR_MESSAGES = {
    NOT_AUTHORIZE: {
        error: "The user is not authorized to log into the Booking Engine Designer UI"
    },
    INTERNAL_ERROR: {
        error: "An internal error occured while logging into the Booking Engine Designer UI. Try again later."
    }
}

/**
 * New implementation using frontman
 * @param creds
 * @param cb
 */
function getToken(creds, cb) {
    const { username, password, token, hotelIdFromCrs = null } = creds;

    if (token) {
        httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else if (isTestEnv) {
        httpClient.defaults.headers.common['Authorization'] = `Basic ${btoa(`${btoa(username)}:${btoa(password)}`)}`;
    } else {
        return false;
    }

    return dispatch => {
        dispatch(actions.start());

        if (token) {
            httpClient.get(`${AUTH_TOKEN}/v1/auth/token`).then(authResponse => {
                if (authResponse.data.tokenActive === 'ACTIVE') {
                    if (hotelIdFromCrs) {
                        return handleTokenResponse(authResponse, cb, _.get(authResponse.data, 'userId'), dispatch, token, hotelIdFromCrs)
                    } else {
                        return handleTokenResponse(authResponse, cb, _.get(authResponse.data, 'userId'), dispatch, token, authResponse.data.DefaultHotel)
                    }
                } else {
                    sessionStorage.removeItem('token');
                    throw new Error('Token invalid or inactive')
                }
            }).catch((e) => {
                cb({ error: e });
                dispatch(actions.error(e));
            });
        } else if (isTestEnv) {
            httpClient.post(`${AUTH_TOKEN}/v1/auth/token`, {
                "UserIPAddress": "123.123.123.123",
                GrantType: "CLIENT_CREDENTIALS",
                BusinessContext: "BE",
                UserType: "Hotel"
            }).then(authResponse => {
                if (authResponse.status !== 200) {
                    cb({ error: e });
                    return Promise.reject({ error: authorizationResponse.statusText });
                }
                else if (authResponse.status === 200 && _.get(authResponse, 'data.error')) {
                    dispatch(actions.error(authResponse.data.Errors[0].Message))
                    return Promise.reject({ error: authResponse.data.Errors[0].Message });
                } else {
                    handleTokenResponse(authResponse, cb, username, dispatch, _.get(authResponse, 'data.access_token'), _.get(authResponse, 'data.ApplicationResult.AccessObject.DefaultHotel'))
                }
            }).catch((e) => {
                cb({ error: e });
                dispatch(actions.error(e));
            });
        }
    };
}

function getAllHotels({ chainId, pageStart = 0, perPage = 26 }, cb, dispatch) {
    return httpClient.get(`${FRONTMAN_URL}/admin/hotels?view=Lookup&pageStart=${pageStart}&pageSize=${perPage}&chainId=${chainId}`)
        .then((response) => {
            if (_.get(response, 'data.ErrorCode', null) === 'SHS.ERR.NO_ACCESS') {
                dispatch({ type: 'OAUTH_SET_ACCESS_LEVELS', userPermissionLevels: undefined });
                cb({ error: ERROR_MESSAGES.NOT_AUTHORIZE });
                return dispatch(actions.error(ERROR_MESSAGES.NOT_AUTHORIZE))
            }

            return response.data.hotels ? response.data.hotels : []
        });
}

function handleTokenResponse(authResponse, cb, username, dispatch, token, defaultHotel) {
    dispatch(actions.username(username));
    httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    httpClient.get(`${FRONTMAN_URL}/admin/hotels/${defaultHotel}`).then(hotelResponse => {
        const chainList = _.get(hotelResponse, 'data.chainList.chain', []);

        const primaryChain = _.find(chainList, elem => elem.primary);
        const primaryChainId = primaryChain.id;

        httpClient.get(`${FRONTMAN_URL}/access/token/acl?view=Complete&chainId=${primaryChainId}`).then((accesspointsResponse => {
            const accessPoints = _.get(accesspointsResponse, 'data.accessPoints.accessPoints', []);
            const accesibility = _.get(accesspointsResponse, 'data.accesibility', {});
            const roles = _.get(accesspointsResponse, 'data.roles.roles', []);
            const chains = _.get(accesibility, 'chains');
            const hotels = _.get(accesibility, 'hotels');
            const allowedChains = chains.chains || [];
            dispatch({ type: 'OAUTH_ROLES', roles });
            dispatch({ type: 'OAUTH_ASSIGNED_CHAINS', chains });

            const allowAccess = _.find(accessPoints, elem => userHasAccessToRequest(elem.id));

            let listOfAccessibleChains = chains.chains;
            if (!listOfAccessibleChains) {
                listOfAccessibleChains = chains.hotels;
            }
            const chainSystemAccess = chains.systemAccess;
            const listOfAccessibleHotels = hotels.hotels;
            const hotelSystemAccess = hotels.systemAccess;

            const currentChain = getCurrentChain(allowedChains, chainList);
            const currentChaindId = currentChain.id;
            const currentChainName = currentChain.name;

            dispatch(actions.setChain(currentChaindId, currentChainName));

            return Promise.all([
                getAllHotels({ chainId: currentChaindId }, cb, dispatch),
                authResponse.data,
                hotelResponse.data
            ]).then(([hotelList, authorization, defaultHotelResponse]) => {
                const doesHotelListHaveDefaultHotel = _.some(hotelList, hotel => hotel.id === defaultHotelResponse.id);
                if (!doesHotelListHaveDefaultHotel && !_.isEmpty(hotelList)) {
                    hotelList[hotelList.length - 1] = defaultHotelResponse;
                }
                const payload = {
                    ...authorization,
                    access_token: token,
                    initialState: {
                        hotellist: hotelList
                    }
                };

                if (listOfAccessibleChains) {
                    if (chainSystemAccess) {
                        if (listOfAccessibleChains.length === 0) {
                            listOfAccessibleChains = [primaryChain];
                            dispatch({ type: 'OAUTH_SET_ACCESS_LEVELS', userPermissionLevels: [SAVE_LEVEL.SYSTEM, SAVE_LEVEL.CHAIN, SAVE_LEVEL.BRAND, SAVE_LEVEL.HOTEL] });
                        }
                    }
                    else if (!chainSystemAccess) {
                        if (listOfAccessibleChains.length > 0) {
                            dispatch({ type: 'OAUTH_SET_ACCESS_LEVELS', userPermissionLevels: [SAVE_LEVEL.CHAIN, SAVE_LEVEL.BRAND, SAVE_LEVEL.HOTEL] });
                        }
                        else if (listOfAccessibleChains.length === 0) {
                            if (hotelSystemAccess) {
                                if (listOfAccessibleHotels.length === 0) {
                                    //placeholder for case execution
                                }
                            }
                            else if (!hotelSystemAccess) {
                                if (listOfAccessibleHotels.length === 0) {
                                    dispatch({ type: 'OAUTH_SET_ACCESS_LEVELS', userPermissionLevels: undefined });
                                    cb({ error: ERROR_MESSAGES.NOT_AUTHORIZE });
                                    dispatch(actions.error(ERROR_MESSAGES.NOT_AUTHORIZE));
                                }
                                else if (listOfAccessibleHotels.length > 0) {
                                    dispatch({ type: 'OAUTH_SET_ACCESS_LEVELS', userPermissionLevels: [SAVE_LEVEL.HOTEL] });
                                }
                            }
                        }
                    }
                }
                else {
                    listOfAccessibleChains = [primaryChain];
                    dispatch({
                        type: 'OAUTH_SET_ACCESS_LEVELS', userPermissionLevels: [SAVE_LEVEL.CHAIN, SAVE_LEVEL.BRAND, SAVE_LEVEL.HOTEL]
                    });
                }

                if (allowAccess) {
                    const accessLevel = _.reduce(accessPoints, (result, currentValue) => {
                        for (const accessPoint in ACCESS_POINTS) {
                            if (ACCESS_POINTS[accessPoint] === currentValue.id) {
                                const accessPointName = accessPoint.split('_');
                                const accessPointLevel = accessPointName.pop();
                                const accessPointNameAfterPop = accessPointName.join('_');
                                const key = _.camelCase(accessPointNameAfterPop);

                                if (!Array.isArray(result[key])) {
                                    result[key] = [];
                                }

                                result[key].push(accessPointLevel);
                            }
                        }

                        return result;
                    }, {})

                    dispatch({ type: 'OAUTH_LOAD_ACCESS_POINTS', accessPointType: 'accessLevel', accessPointValue: accessLevel });

                    dispatch(actions.complete(payload, () => {
                        cb({ data: payload, defaultHotel: defaultHotel, accessibleChains: listOfAccessibleChains });
                    }), {
                        token
                    });
                }
                else {
                    flagAnError(cb, dispatch, ERROR_MESSAGES.NOT_AUTHORIZE);
                }

            })
        }))
    }, error => {
        flagAnError(cb, dispatch, error);
    }).catch(() => {
        flagAnError(cb, dispatch, ERROR_MESSAGES.INTERNAL_ERROR);
    })
}

function getCurrentChain(accesibleChains, hotelChains) {
    const primaryChain = _.find(hotelChains, chain => chain.primary);

    const hasAccessToPrimaryChain = primaryChain ? _.some(accesibleChains, chain => chain.id === primaryChain.id) : false;
    if (hasAccessToPrimaryChain) {
        return primaryChain;
    }

    for (const hotelChain of hotelChains) {
        if (_.find(accesibleChains, accesibleChain => accesibleChain.id === hotelChain.id)) {
            return hotelChain;
        }
    }

    return primaryChain || _.head(hotelChains);
}

function flagAnError(cb, dispatch, error) {
    cb({ error });
    dispatch(actions.error(error));
}

function add_axios_header(data) {
    httpClient.defaults.headers.common.Authorization = `Bearer ${data.access_token}`;
}

function add_axios_interceptors() {
    const requestTime = moment().valueOf()
    httpClient.interceptors.request.use((config) => {
        postRequestToSplunk(config.method, config.url, requestTime)
        return config
    },
        () => {
            console.log('request failed')
        })
    httpClient.interceptors.response.use((response) => {
        postResponseToSplunkSuccess(response.config.method, response.config.url, (moment().valueOf() - requestTime) / 1000 + 'sec', response.status)
        return response
    },
        (response) => {
            postResponseToSplunkFailed(response.config.method, response.config.url, (moment().valueOf() - requestTime) / 1000 + 'sec', response.message)
            return Promise.reject(response)
        })
}



// WEBPACK FOOTER //
// ./lib/redux-oauth2.js